import React, { useCallback, useEffect } from 'react'
import { useQuery, useReactiveVar } from '@apollo/client'
import { Tooltip } from '@wmgtech/legato'

import { ProductsHeader, ProductsList } from 'modules/Products/components'

import { productsFiltersVar, productSortFieldVar } from 'modules/Products/store'
import { PRODUCTS_LIST } from 'modules/Products/graphql/queries/productsList'

import { labelCodeIdVar, spaceIdVar } from 'modulesCommon/store'
import { DEFAULT_ENTITIES_LIMIT } from 'shared/consts'
import { useFormattedZoneList, useLoading } from 'shared/hooks'
import { LoadMore, NoItems, SkeletonLoader } from 'components'
import { FilterProducts } from 'modulesCommon/Filters/containers'
import { useZonesList } from 'modulesCommon/serviceHooks'

const ProductsListContainer = () => {
  const labelCodeId = useReactiveVar(labelCodeIdVar)
  const productsFilters = useReactiveVar(productsFiltersVar)
  const productSortField = useReactiveVar(productSortFieldVar)
  const spaceId = useReactiveVar(spaceIdVar)
  const { zoneList } = useZonesList()
  const { units } = useFormattedZoneList(zoneList, labelCodeId)

  const { label_id, ...restProductsFilters } = productsFilters

  const createInputFilter = useCallback(
    (offset: number) => ({
      variables: {
        input: {
          offset: offset,
          limit: DEFAULT_ENTITIES_LIMIT,
          ...(productSortField.fieldName.length && {
            order: {
              direction: productSortField.direction ? 'ASC' : 'DESC',
              fieldName: productSortField.fieldName
            }
          }),
          filters: {
            ...restProductsFilters,
            ...(spaceId && !labelCodeId && !label_id.length && { space: spaceId }),
            ...(label_id?.length && label_id !== 'all' && { label_id: label_id }),
            ...(labelCodeId && !spaceId && { label_id: labelCodeId })
          }
        }
      }
    }),
    [productSortField.fieldName, productSortField.direction, restProductsFilters, spaceId, labelCodeId, label_id]
  )

  const sortProductsByField = useCallback(
    (fieldNameChanged) => () => {
      const { direction, fieldName } = productSortField

      productSortFieldVar({
        direction: fieldName === fieldNameChanged ? !direction : true,
        fieldName: fieldNameChanged
      })
    },
    [productSortField]
  )

  const {
    data: productsListFetched,
    refetch,
    fetchMore,
    loading: productsListLoading
  } = useQuery(PRODUCTS_LIST, {
    ...createInputFilter(0),
    notifyOnNetworkStatusChange: true
  })

  const productsList = productsListFetched?.getNewReleases?.products
  const productsTotal= productsListFetched?.getNewReleases?.total

  const increaseReleaseOffset = useCallback(
    () => fetchMore(createInputFilter(productsList?.length || 0)),
    [createInputFilter, fetchMore, productsList?.length]
  )

  useEffect(() => {
    refetch()
  }, [refetch, productsFilters, labelCodeId, productSortField])

  useLoading({ isLoading: productsListLoading })

  return (
    <>
      <ProductsHeader
        sortProducts={sortProductsByField}
        productSortField={productSortField}
        isSpace={!!spaceId}
      >
        <Tooltip message="Filter Products" placement="top" colorType="dark">
          <FilterProducts productsTotal={productsTotal}/>
        </Tooltip>
      </ProductsHeader>
      {!productsList?.length && productsListLoading && <SkeletonLoader />}
      {productsList?.length ?
        <>
          <ProductsList productsList={productsList} units={units} />
          <LoadMore
            onLoad={increaseReleaseOffset}
            isItemsLoading={productsListLoading}
            itemsTotal={productsTotal}
            itemsCount={productsList?.length}
            label="releases"
          />
        </>
        : <NoItems name="Products" />
      }
    </>
  )
}

export default ProductsListContainer
